import React, { useRef, useState } from "react";
import { FIELD_CENTER, FIELD_RADIUS, MAX_FIELDERS, clampToField, fromFractions, isInsideField, toFieldUnits, toFractions } from "./fieldPlacement";
import type { FieldPoint } from "./fieldPlacement";
import "./FieldPlacementDialog.scss";

interface Chip extends FieldPoint {
  id: number;
}

interface FieldPlacementDialogProps {
  // Fractions (0..1) of the last placement sent, so the streamer can adjust rather than start over.
  initial?: FieldPoint[];
  onClose: () => void;
  // Called with the placement as 0..1 fractions; the dialog stays open until the caller closes it.
  onSubmit: (fielders: FieldPoint[]) => void | Promise<void>;
}

const CHIP_RADIUS = 3.2;
const HIGHLIGHT_CHIP_RADIUS = 4;
// Below this much pointer movement (in the same 0..100 field units as positions), a pointerdown +
// pointerup on a chip is a click (toggles highlight) rather than a drag (moves it).
const CLICK_MOVEMENT_THRESHOLD = 1.5;

// "Field Placement" dialog: the streamer marks where each fielder stands on a top-down field. Click on
// the field to add a fielder (up to eleven), drag one to move it, double-click one to remove it, or
// click one in place (no drag) to highlight it — the one fielder called out on the ticker, ringed in
// gold there. At most one highlighted at a time; clicking a highlighted chip un-highlights it. Only
// positions (and which one is highlighted) are sent — the ticker draws the same field, no names.
const FieldPlacementDialog: React.FC<FieldPlacementDialogProps> = ({ initial, onClose, onSubmit }) => {
  const nextId = useRef(0);
  const svgRef = useRef<SVGSVGElement>(null);
  const [chips, setChips] = useState<Chip[]>(() => fromFractions(initial ?? []).map((p) => ({ ...p, id: nextId.current++ })));
  const [highlightedId, setHighlightedId] = useState<number | null>(() => {
    const seeded = fromFractions(initial ?? []);
    const idx = seeded.findIndex((p) => p.highlighted);
    return idx === -1 ? null : idx;
  });
  const [dragId, setDragId] = useState<number | null>(null);
  // Set on pointerdown, cleared on pointerup — lets handleMove tell a drag from a click so a
  // chip's own pointer handlers don't have to duplicate that distinction.
  const dragMovedRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const pointFromEvent = (e: { clientX: number; clientY: number }): FieldPoint | null => {
    const rect = svgRef.current?.getBoundingClientRect();
    return rect && rect.width > 0 ? toFieldUnits(e.clientX, e.clientY, rect) : null;
  };

  const handleFieldClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const point = pointFromEvent(e);
    if (!point || !isInsideField(point) || chips.length >= MAX_FIELDERS) return;
    setChips((prev) => [...prev, { ...point, id: nextId.current++ }]);
  };

  const startDrag = (e: React.PointerEvent<SVGCircleElement>, id: number) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragMovedRef.current = false;
    setDragId(id);
  };

  const handleMove = (e: React.PointerEvent<SVGCircleElement>) => {
    if (dragId === null) return;
    const point = pointFromEvent(e);
    if (!point) return;
    const chip = chips.find((c) => c.id === dragId);
    if (chip && Math.hypot(point.x - chip.x, point.y - chip.y) > CLICK_MOVEMENT_THRESHOLD) {
      dragMovedRef.current = true;
    }
    const clamped = clampToField(point);
    setChips((prev) => prev.map((c) => (c.id === dragId ? { ...c, ...clamped } : c)));
  };

  const endDrag = (id: number) => {
    if (!dragMovedRef.current) {
      setHighlightedId((prev) => (prev === id ? null : id));
    }
    setDragId(null);
  };

  const removeChip = (id: number) => {
    setChips((prev) => prev.filter((c) => c.id !== id));
    setHighlightedId((prev) => (prev === id ? null : prev));
  };

  const submit = async () => {
    if (chips.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(toFractions(chips.map((c) => ({ ...c, highlighted: c.id === highlightedId }))));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sd-dialog-overlay" onClick={onClose}>
      <div className="sd-dialog sd-dialog--field" onClick={(e) => e.stopPropagation()}>
        <div className="sd-dialog__header">
          <h3 className="sd-dialog__title">Field Placement</h3>
          <button type="button" className="sd-dialog__close" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="sd-dialog__label">
          Click on the field to place a fielder. Drag to move one, click one in place to highlight it
          (ringed on the ticker), double-click to remove it.
          <strong className="sd-field__count">
            {chips.length} / {MAX_FIELDERS}
          </strong>
        </p>

        <div className="sd-field">
          <span className="sd-field__end sd-field__end--top">Bowler&apos;s end</span>
          <svg ref={svgRef} className="sd-field__svg" viewBox="0 0 100 100" onClick={handleFieldClick} role="img" aria-label="Cricket field">
            <circle className="sd-field__grass" cx={FIELD_CENTER} cy={FIELD_CENTER} r={FIELD_RADIUS} />
            <circle className="sd-field__ring" cx={FIELD_CENTER} cy={FIELD_CENTER} r={27} />
            <rect className="sd-field__pitch" x="48" y="43" width="4" height="14" rx="0.6" />
            {chips.map((chip) => (
              <circle
                key={chip.id}
                className={`sd-field__chip${dragId === chip.id ? " sd-field__chip--drag" : ""}${
                  highlightedId === chip.id ? " sd-field__chip--highlight" : ""
                }`}
                cx={chip.x}
                cy={chip.y}
                r={highlightedId === chip.id ? HIGHLIGHT_CHIP_RADIUS : CHIP_RADIUS}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  removeChip(chip.id);
                }}
                onPointerDown={(e) => startDrag(e, chip.id)}
                onPointerMove={handleMove}
                onPointerUp={() => endDrag(chip.id)}
                onPointerCancel={() => setDragId(null)}
              />
            ))}
          </svg>
          <span className="sd-field__end sd-field__end--bottom">Batter&apos;s end</span>
        </div>

        <div className="sd-dialog__actions sd-field__actions">
          <button type="button" className="sd-dialog__btn-cancel" disabled={chips.length === 0} onClick={() => setChips((prev) => prev.slice(0, -1))}>
            Undo
          </button>
          <button type="button" className="sd-dialog__btn-cancel" disabled={chips.length === 0} onClick={() => setChips([])}>
            Clear
          </button>
          <button type="button" className="sd-dialog__btn-confirm" disabled={chips.length === 0 || submitting} onClick={submit}>
            Show on Ticker
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldPlacementDialog;
